import {
  notificationMessage,
  useNotificationStore,
} from 'state/notificationstate/notificationstate';
import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  InfoCircleFilled,
  WarningFilled,
} from '@ant-design/icons';
import React, { MutableRefObject, useState, useLayoutEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useSmallScreen } from 'helpers/isSmallScreen';
import { Modal } from 'antd';
import { useHistory } from 'react-router';
import { Trans } from 'react-i18next';

export interface NotificationProps {
  parent: MutableRefObject<any>;
}

const SmallNotification = (
  message: notificationMessage,
  isSmallScreen: boolean
) => {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.3 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.5, transition: { duration: 0.2 } }}
      key={message.tid}
      style={{
        pointerEvents: 'none',
        margin: isSmallScreen ? '8px' : '16px',
        padding: '4px',
        borderRadius: '4px',
        display: 'flex',
        alignContent: 'center',
        alignItems: 'center',
        background: Theme['@white'],
        maxWidth: 320,
        alignSelf: message.position === 'left' ? 'start' : 'end',
        fontSize: '12px'
      }}
      className={'ant-message-notice-content'}
    >
      <div style={{alignSelf: 'flex-start'}}>
        {message.type === 'error' && <ExclamationCircleFilled />}
        {message.type === 'warning' && <WarningFilled />}
        {message.type === 'info' && <InfoCircleFilled />}
        {message.type === 'success' && <CheckCircleFilled />}
      </div>

      <span style={{ margin: '0 12px' }}>
        {Array.isArray(message.message) ? (
          message.message.reduce(
            (prev: string, current: string) =>
              `${prev} ${(
                <p>
                  <Trans>{current}</Trans>
                </p>
              )}`
          )
        ) : (
          <Trans>{message.message}</Trans>
        )}
      </span>
    </motion.div>
  );
};

const ModalNotification = (m: notificationMessage) => {
  const history = useHistory();
  const text = Array.isArray(m.message) ? (
    m.message.map((mess) => (
      <p>
        <Trans>{mess}</Trans>
      </p>
    ))
  ) : (
    <Trans>{m.message}</Trans>
  );

  return (
    <Modal
      style={{ zIndex: 1000 }}
      closable={false}
      cancelText={''}
      onOk={(e) => {
        if (m.persistent) {
          if (m.onOk) {
            m.onOk();
            history.push('/');
          }
        }
      }}
      visible
    >
      {text}
    </Modal>
  );
};

const Notification: React.FunctionComponent<NotificationProps> = ({
  parent,
}) => {
  const [top, setTop] = useState<number>();
  const [left, setLeft] = useState<number>();
  const [width, setWidth] = useState<number>();
  const isSmallScreen = useSmallScreen();

  const nStore = useNotificationStore();

  useLayoutEffect(() => {
    if (!parent.current) {
      return;
    }
    const updatePosition = () => {
      setTop(parent?.current?.getBoundingClientRect().top);
      setLeft(parent?.current?.getBoundingClientRect().left);
      setWidth(parent?.current?.getBoundingClientRect().width);
    };
    window.addEventListener('resize', updatePosition);
    updatePosition();
    return () => window.removeEventListener('resize', updatePosition);
  }, [parent, nStore.messages]);

  return (
    <div
      style={{
        display: 'flex',
        position: 'absolute',
        zIndex: nStore.messages.length > 0 ? 100 : 0,
        top,
        left,
        width,
        height: '300px',
        pointerEvents: 'none',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <AnimatePresence>
          {nStore.messages
            .filter((m) => m.size === 'small')
            .slice(0, 5)
            .map((m) => {
              return SmallNotification(m, isSmallScreen);
            })}
        </AnimatePresence>
      </div>
      <AnimatePresence>
        {nStore.messages
          .filter((m) => m.size === 'modal')
          .map((m) => {
            return ModalNotification(m);
          })}
      </AnimatePresence>
    </div>
  );
};

export default Notification;
